import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { internationalDateFormat } from 'src/helper/helper';
import type {
  FilterGroups,
  FilterValue,
} from 'src/components/app/context/FilterContext';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import { ORG_UNIT_FILTER_NAME } from 'src/components/common/OrgTree/types';
import type { DateRangeAsStrings } from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';
import { filterToDateRange } from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';

import { FILTER_KEYS } from './constants';
import {
  mapDemographicsFilters,
  mapFollowUpFilters,
} from './useFollowUpFilters';

const formatDateRange = (
  filterGroups: FilterGroups,
  key: string,
): string | undefined => {
  const unformattedDateRange = filterGroups[key]?.[0];
  if (!unformattedDateRange) return undefined;

  const { startDate, endDate } = filterToDateRange(
    unformattedDateRange as FilterValue<DateRangeAsStrings>,
  );
  return `${internationalDateFormat(new Date(startDate))} - ${internationalDateFormat(new Date(endDate))}`;
};

export interface UseFollowUpFiltersVerboseReturn {
  orgUnits?: string;
  demographicsFilters?: string;
  followUpFilters?: string;
  dateRange?: string;
  comparisonDateRange?: string;
}

const useFollowUpFiltersVerbose = (): UseFollowUpFiltersVerboseReturn => {
  const { filterGroups } = useFilterContext();
  const { t } = useTranslation('followUp');

  const orgUnits = useMemo(
    () =>
      filterGroups[ORG_UNIT_FILTER_NAME]?.map(
        (orgUnit) => orgUnit.label,
      ).toString(),
    [filterGroups],
  );

  const demographicsFilters = useMemo(
    () =>
      Object.values(mapDemographicsFilters(filterGroups, t, 'labels'))
        .filter((value) => value !== undefined)
        .join('; '),
    [filterGroups, t],
  );

  const followUpFilters = useMemo(
    () =>
      Object.values(mapFollowUpFilters(filterGroups, t, 'labels'))
        .filter((value) => value !== undefined)
        .join('; '),
    [filterGroups, t],
  );

  const dateRange = useMemo(
    () => formatDateRange(filterGroups, FILTER_KEYS.DATE_RANGE),
    [filterGroups],
  );

  const comparisonDateRange = useMemo(
    () => formatDateRange(filterGroups, FILTER_KEYS.DATE_RANGE_COMPARISON),
    [filterGroups],
  );

  return {
    orgUnits,
    demographicsFilters,
    followUpFilters,
    dateRange,
    comparisonDateRange,
  };
};

export default useFollowUpFiltersVerbose;
